import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { BridgeIframe } from '#src/components/offRamp/OffRampKycOnboarding/BridgeIframe/index.tsx';
import { OffRampStepLayout } from '#src/components/offRamp/OffRampKycOnboarding/OffRampStepLayout/index.tsx';
import { OFF_RAMP_ONBOARDING_PATH } from '#src/components/offRamp/OffRampKycOnboarding/type.ts';
import { useCreateOffRampBridgeTosLink } from '#src/components/offRamp/OffRampKycOnboarding/useCreateOffRampBridgeTosLink.tsx';
import { ORIGIN } from '#src/config.ts';

type Props = {
  onComplete: (signedAgreementId: string) => void;
};

const extractSignedAgreementId = (data: unknown): string | null => {
  if (data && typeof data === 'object' && 'signedAgreementId' in data) {
    const { signedAgreementId } = data as { signedAgreementId: unknown };
    if (typeof signedAgreementId === 'string') return signedAgreementId;
  }
  return null;
};

export const TermsStep = ({ onComplete }: Props) => {
  const createTosLink = useCreateOffRampBridgeTosLink();

  const [tosUrl, setTosUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onContinue = async () => {
    setError(null);
    const result = await createTosLink(`${ORIGIN}${OFF_RAMP_ONBOARDING_PATH}`);

    const payload = result.data?.createOffRampBridgeTosLink;
    if (payload?.errors?.length) {
      setError(payload.errors[0].message);
      return;
    }
    if (payload?.tosUrl) setTosUrl(payload.tosUrl);
  };

  const onMessage = (data: unknown) => {
    const signedAgreementId = extractSignedAgreementId(data);
    if (!signedAgreementId) return;
    setTosUrl(null);
    onComplete(signedAgreementId);
  };

  if (tosUrl) {
    return (
      <BridgeIframe
        url={tosUrl}
        onMessage={onMessage}
        onClose={() => setTosUrl(null)}
      />
    );
  }

  return (
    <OffRampStepLayout
      title={<FormattedMessage defaultMessage="Accept the terms of service" />}
      description={
        <FormattedMessage defaultMessage="To cash out to your Mangopay Cash Wallet, you first need to accept our payment partner Bridge's terms of service." />
      }
    >
      <Vertical gap={2}>
        {!!error && <BodyM color="var(--c-red-400)">{error}</BodyM>}
        <Button size="medium" fullWidth onClick={onContinue}>
          <FormattedMessage defaultMessage="Continue to Bridge" />
        </Button>
      </Vertical>
    </OffRampStepLayout>
  );
};
